import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from "./config/api";
import "./styles/FeedbackForm.css";

 type RatingValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

 const ratingValues: RatingValue[] = [1,2,3,4,5,6,7,8,9,10];

 const ratingColors: string[] = [
     "#d32f2f",
     "#e04b2e",
     "#e76f2e",
     "#eb8f2d",
     "#f0b22c",
     "#d2c72a",
     "#a8c92a",
     "#7dc82b",
     "#4ab83a",
     "#28a745",
 ];

 const hexToRgba = (hex: string, alpha: number): string => {
     const h = hex.replace('#','');
     const bigint = parseInt(h, 16);
     const r = (bigint >> 16) & 255;
     const g = (bigint >> 8) & 255;
     const b = bigint & 255;
     return `rgba(${r}, ${g}, ${b}, ${alpha})`;
 };

 const FeedbackForm: React.FC = () => {
	const { dealId } = useParams<{ dealId: string }>();
    const location = useLocation();
	const [supportSatisfaction, setSupportSatisfaction] = useState<RatingValue | null>(null);
	const [processingSatisfaction, setProcessingSatisfaction] = useState<RatingValue | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const responsibleUserId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const fromQuery = params.get("responsible_user_id");
        if (fromQuery && fromQuery.trim().length > 0) {
            const parsed = parseInt(fromQuery, 10);
            return !isNaN(parsed) ? parsed : null;
        }
        return null;
    }, [location.search]);

    useEffect(() => {
        if (!submitError && !submitSuccess) return;
        const timer = setTimeout(() => {
            setSubmitError(null);
            setSubmitSuccess(false);
        }, 4000);
        return () => clearTimeout(timer);
    }, [submitError, submitSuccess]);

	const completionPercent = useMemo(() => {
		const total = 2;
		let filled = 0;
		if (supportSatisfaction) filled += 1;
		if (processingSatisfaction) filled += 1;
		return Math.round((filled / total) * 100);
	}, [supportSatisfaction, processingSatisfaction]);

	const isDealIdValid = useMemo(() => {
		return typeof dealId === "string" && dealId.trim().length > 0;
	}, [dealId]);

	const handleSelectSupport = (value: RatingValue) => {
		setSupportSatisfaction(value);
	};

	const handleSelectProcessing = (value: RatingValue) => {
		setProcessingSatisfaction(value);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitError(null);
		setSubmitSuccess(false);

		if (!isDealIdValid) {
			setSubmitError("Некорректная ссылка формы.");
			return;
		}
		if (!supportSatisfaction || !processingSatisfaction) {
			setSubmitError("Пожалуйста, ответьте на оба вопроса.");
			return;
		}

		setIsSubmitting(true);
		try {
			await axios.post(
				`${API_CONFIG.BASE_URL}/api/v1/feedback/${dealId}/`,
				{
					deal_id: dealId,
					responsible_user_id: responsibleUserId,
					satisfied_support: supportSatisfaction,
					satisfied_processing_time_request: processingSatisfaction,
				}
			);
			setSubmitSuccess(true);
		} catch (error: any) {
			const message = error?.response?.data?.detail || "Не удалось отправить форму. Попробуйте позже.";
			setSubmitError(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="feedback-wrapper" role="main" aria-label="Форма обратной связи">
			<form className="feedback-form" onSubmit={handleSubmit} aria-describedby="form-desc">
				<header className="feedback-header">
					<h1 className="feedback-title">Оцените качество нашей работы {dealId ? `по сделке №${dealId}` : "по сделке"}</h1>
					<p id="form-desc" className="feedback-subtitle">Пожалуйста, ответьте на 2 вопроса. Поля, отмеченные <span className="required" aria-hidden="true">*</span>, обязательны.</p>
					<div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={completionPercent} aria-label="Прогресс заполнения">
						<div className="progress-bar" style={{ width: `${completionPercent}%` }} />
					</div>
				</header>

				<input type="hidden" name="deal_id" value={dealId || ""} />

				<section className="question" aria-labelledby="q1-label">
					<div className="question-head">
						<label id="q1-label" className="question-label">
							<span className="required" aria-hidden="true">*</span> Удовлетворены ли Вы поддержкой?
						</label>
					</div>
					<div className="scale">
						<span className="scale-edge" aria-hidden="true">Совершенно не удовлетворен</span>
                        <div className="scale-options" role="radiogroup" aria-label="Оценка поддержки от 1 до 10">
                            {ratingValues.map((value, idx) => {
                                const color = ratingColors[idx];
                                const bg = hexToRgba(color, 0.12);
                                return (
                                <label
                                    key={value}
                                    className={`scale-option ${supportSatisfaction === value ? "selected" : ""}`}
                                    tabIndex={0}
                                    aria-label={`Оценка ${value}`}
                                    onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { handleSelectSupport(value); } }}
                                    style={{ ['--chip-color' as any]: color, ['--chip-bg' as any]: bg }}
                                >
									<input
										type="radio"
										name="support_satisfaction"
										value={value}
										checked={supportSatisfaction === value}
										onChange={() => handleSelectSupport(value)}
										aria-checked={supportSatisfaction === value}
									/>
									<span className="chip" aria-hidden="true">{value}</span>
									<span className="visually-hidden">{`Оценка ${value}`}</span>
                                </label>
                            );})}
						</div>
					</div>
				</section>

				<section className="question" aria-labelledby="q2-label">
					<div className="question-head">
						<label id="q2-label" className="question-label">
							<span className="required" aria-hidden="true">*</span> Удовлетворены ли Вы сроком обработки обращения?
						</label>
					</div>
					<div className="scale">
						<span className="scale-edge" aria-hidden="true">Совершенно не удовлетворен</span>
                        <div className="scale-options" role="radiogroup" aria-label="Оценка срока обработки от 1 до 10">
                            {ratingValues.map((value, idx) => {
                                const color = ratingColors[idx];
                                const bg = hexToRgba(color, 0.12);
                                return (
                                <label
                                    key={value}
                                    className={`scale-option ${processingSatisfaction === value ? "selected" : ""}`}
                                    tabIndex={0}
                                    aria-label={`Оценка ${value}`}
                                    onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { handleSelectProcessing(value); } }}
                                    style={{ ['--chip-color' as any]: color, ['--chip-bg' as any]: bg }}
                                >
									<input
										type="radio"
										name="processing_satisfaction"
										value={value}
										checked={processingSatisfaction === value}
										onChange={() => handleSelectProcessing(value)}
										aria-checked={processingSatisfaction === value}
									/>
									<span className="chip" aria-hidden="true">{value}</span>
									<span className="visually-hidden">{`Оценка ${value}`}</span>
                                </label>
                            );})}
						</div>
					</div>
				</section>


				<div className="actions">
					<button
						type="submit"
						className="submit-btn"
						disabled={isSubmitting}
						aria-disabled={isSubmitting}
					>
						{isSubmitting ? "Отправка..." : "Отправить"}
					</button>
				</div>
			</form>
            <div className="toast-container" aria-live="polite" aria-atomic="true">
                {submitError && (
                    <div className="toast toast-error" role="alert">{submitError}</div>
                )}
                {submitSuccess && (
                    <div className="toast toast-success" role="status">Спасибо! Ваши ответы отправлены.</div>
                )}
            </div>
		</div>
	);
 };

 export default FeedbackForm;
